const MAX_SESSION_MS = 400 * 24 * 60 * 60 * 1000; // ignore absurd exp values (>~1 year)

/** Decode JWT `exp` to an absolute expiry timestamp (ms). Returns null if not a usable JWT. */
export function getTokenExpiryMs(token: string | null): number | null {
  if (!token || !token.includes(".")) return null;

  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };

    if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
      return null;
    }

    // `exp` is seconds since epoch (~1.7e9); milliseconds (~1.7e12)
    let expiryMs =
      payload.exp > 1e12 ? payload.exp : payload.exp * 1000;

    const now = Date.now();
    const msUntil = expiryMs - now;

    // Reject garbage: unreasonably far in the future
    if (msUntil > MAX_SESSION_MS) {
      return null;
    }

    return expiryMs;
  } catch {
    return null;
  }
}

export function getMsUntilExpiry(token: string | null): number | null {
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs == null) return null;
  return expiryMs - Date.now();
}

export function isSessionExpired(token: string | null): boolean {
  const msUntil = getMsUntilExpiry(token);
  return msUntil != null && msUntil <= 0;
}

export function formatCountdownMmSs(msRemaining: number): string {
  const totalSec = Math.max(0, Math.min(Math.floor(msRemaining / 1000), 99 * 60 + 59));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const SNOOZE_KEY = "reliablyme_session_warning_snooze_until";

export function snoozeSessionWarning(durationMs: number): void {
  sessionStorage.setItem(
    SNOOZE_KEY,
    String(Date.now() + durationMs)
  );
}

export function isSessionWarningSnoozed(): boolean {
  const raw = sessionStorage.getItem(SNOOZE_KEY);
  if (!raw) return false;
  const until = Number(raw);
  if (!Number.isFinite(until) || Date.now() >= until) {
    sessionStorage.removeItem(SNOOZE_KEY);
    return false;
  }
  return true;
}

export function clearSessionWarningSnooze(): void {
  sessionStorage.removeItem(SNOOZE_KEY);
}
